import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FolderPlus, Sparkles, Type, Captions, Shapes, Combine, Wand2, Sliders,
  Scissors, Undo2, Redo2, Play, Pause, SkipBack, SkipForward, StepBack, StepForward,
  ChevronDown, Trash2, Copy, Split, Magnet, ZoomIn, ZoomOut, Triangle,
  Crop, Music2, Gauge, MoveUpRight, X,
} from "lucide-react";
import { cx } from "@/components/shared";
import { Logo } from "@/components/brand/Logo";

const TOOLS = [
  { id: "media", label: "Media", icon: FolderPlus },
  { id: "ai_tools", label: "AI Tools", icon: Sparkles },
  { id: "text", label: "Text", icon: Type },
  { id: "subtitles", label: "Subtitles", icon: Captions },
  { id: "elements", label: "Elements", icon: Shapes },
  { id: "transitions", label: "Transitions", icon: Combine },
  { id: "effects", label: "Effects", icon: Wand2 },
  { id: "filters", label: "Filters", icon: Sliders },
];

const AI_CARDS = [
  { id: "auto-cut", title: "AI Auto Cut", sub: "Automatically detect highlights", icon: Scissors, color: "#5B4DFF" },
  { id: "scene-detect", title: "AI Scene Detect", sub: "Smart scene segmentation", icon: Split, color: "#00B8D9" },
  { id: "color-enhance", title: "AI Color Enhance", sub: "Optimize color and lighting", icon: Sparkles, color: "#10B981" },
  { id: "audio-enhance", title: "AI Audio Enhance", sub: "Improve voice clarity", icon: Music2, color: "#F59E0B" },
  { id: "subtitle", title: "AI Subtitle", sub: "Auto-generate subtitles", icon: Captions, color: "#8B5CF6" },
  { id: "bg-remove", title: "AI Background Remove", sub: "Remove background in one click", icon: Crop, color: "#EC4899" },
];

const RULER = ["00:00:00", "00:00:05:00", "00:00:10:00", "00:00:15:00", "00:00:20:00", "00:00:25:00", "00:00:30:00", "00:00:35:00"];

const V1_CLIPS = [
  { left: "1%", width: "17%", from: "#1e3a8a", to: "#7c3aed" },
  { left: "19%", width: "16%", from: "#b45309", to: "#7c2d12" },
  { left: "36%", width: "17%", from: "#5b21b6", to: "#1e1b4b" },
  { left: "54%", width: "16%", from: "#0e7490", to: "#4c1d95" },
  { left: "71%", width: "12%", from: "#6d28d9", to: "#831843" },
  { left: "84%", width: "15%", from: "#155e75", to: "#5b21b6" },
];
const V2_CLIPS = [
  { left: "6%", width: "11%", from: "#7c3aed", to: "#2563eb" },
  { left: "18%", width: "10%", from: "#c026d3", to: "#4338ca" },
  { left: "66%", width: "12%", from: "#7c3aed", to: "#db2777" },
];
const SUBS = [
  { left: "1%", width: "20%", text: "The world is changing" },
  { left: "24%", width: "22%", text: "and AI is transforming" },
  { left: "49%", width: "18%", text: "the way we create." },
  { left: "69%", width: "20%", text: "Welcome to the future." },
];

function Waveform({ color }) {
  const bars = Array.from({ length: 90 });
  return (
    <div className="flex items-center gap-[2px] h-6 w-full overflow-hidden">
      {bars.map((_, i) => {
        const h = 20 + Math.abs(Math.sin(i * 0.7) * 60) + (i % 5 === 0 ? 20 : 0);
        return <span key={i} style={{ height: `${Math.min(100, h)}%`, background: color }} className="w-[2px] rounded-full opacity-80" />;
      })}
    </div>
  );
}

function TransformRow({ label, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-xs text-slate-400">{label}</span>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}

export default function EditorStudio() {
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState("ai_tools");
  const [playing, setPlaying] = useState(false);
  const [tab, setTab] = useState("Video");
  const [aiEnhance, setAiEnhance] = useState(true);
  const [scale, setScale] = useState(100);
  const [opacity, setOpacity] = useState(100);
  const [zoom, setZoom] = useState(50);

  return (
    <div className="fixed inset-0 flex flex-col bg-[#0B0B12] text-white overflow-hidden cine-grain" data-testid="editor-studio">
      {/* ── Workspace Top Bar ── */}
      <header className="h-14 shrink-0 bg-[#0B0B12]/95 backdrop-blur-xl border-b border-white/10 px-4 flex items-center justify-between z-50">
        <div className="flex items-center gap-2.5">
          <button onClick={() => navigate("/")} data-testid="editor-brand" className="flex items-center gap-2.5">
            <Logo height={26} />
          </button>
        </div>
        <div className="hidden md:flex items-center gap-2 text-sm text-slate-300">
          <span className="text-slate-500">Project:</span>
          <span className="font-medium">Future Vision</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-300" data-testid="editor-saved">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Saved
          </div>
          <div className="flex items-center gap-1">
            <button className="p-1.5 rounded-md hover:bg-white/10 text-slate-400" data-testid="editor-undo"><Undo2 className="w-4 h-4" /></button>
            <button className="p-1.5 rounded-md hover:bg-white/10 text-slate-400" data-testid="editor-redo"><Redo2 className="w-4 h-4" /></button>
          </div>
          <button className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs font-mono" data-testid="editor-aspect">
            16:9 <ChevronDown className="w-3 h-3" />
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-[#5B4DFF] hover:bg-[#4B3DE6] text-white font-semibold text-sm shadow-[0_0_18px_rgba(91,77,255,0.45)] transition" data-testid="editor-export">
            <Sparkles className="w-3.5 h-3.5" /> Export
          </button>
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#5B4DFF] to-[#7C70FF] ring-2 ring-white/10" data-testid="editor-avatar" />
        </div>
      </header>

      {/* ── Preview-only notice — this suite is a visual preview, not yet wired to real projects/media ── */}
      <div
        className="shrink-0 bg-[#5B4DFF]/10 border-b border-[#5B4DFF]/25 px-4 py-2 flex items-center gap-2 text-xs text-[#c3b9ff]"
        data-testid="editor-preview-banner"
      >
        <Sparkles className="w-3.5 h-3.5 shrink-0" />
        <span>
          <strong className="text-white font-semibold">Preview only</strong> — this editing suite is a visual preview of the upcoming editor. It isn’t connected to your projects or media yet, and nothing here is saved.
        </span>
      </div>

      {/* ── Middle: rail | panel | preview | inspector ── */}
      <div className="flex-1 flex min-h-0">
        {/* Left tool rail */}
        <nav className="w-16 shrink-0 bg-[#07070D] border-r border-white/5 flex flex-col items-center py-3 gap-1 overflow-y-auto">
          {TOOLS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTool(t.id)}
              data-testid={`tool-rail-${t.id}`}
              className={cx(
                "w-14 py-2 rounded-lg flex flex-col items-center gap-1 transition-all",
                activeTool === t.id ? "bg-[#5B4DFF]/20 text-white shadow-[0_0_16px_rgba(91,77,255,0.25)]" : "text-slate-500 hover:text-white hover:bg-white/5"
              )}
            >
              <t.icon className="w-[18px] h-[18px]" />
              <span className="text-[9px] leading-none">{t.label}</span>
            </button>
          ))}
        </nav>

        {/* Tool panel (AI Tools) */}
        <aside className="w-72 shrink-0 bg-[#0F0F1A] border-r border-white/10 flex flex-col" data-testid="tool-panel">
          <div className="h-12 px-4 flex items-center justify-between border-b border-white/10">
            <span className="font-semibold text-sm">AI Tools</span>
            <button className="p-1 rounded-md text-slate-500 hover:text-white hover:bg-white/10" data-testid="tool-panel-close"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3 p-3 overflow-y-auto">
            {AI_CARDS.map((c) => (
              <button
                key={c.id}
                data-testid={`ai-card-${c.id}`}
                className="text-left bg-[#161626] hover:bg-[#1E1E34] border border-white/5 hover:border-[#5B4DFF]/40 rounded-xl p-3 transition-all group"
              >
                <div className="w-9 h-9 rounded-lg grid place-items-center mb-2" style={{ background: `${c.color}22`, border: `1px solid ${c.color}55` }}>
                  <c.icon className="w-[18px] h-[18px]" style={{ color: c.color }} />
                </div>
                <div className="text-[13px] font-semibold leading-tight">{c.title}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{c.sub}</div>
              </button>
            ))}
          </div>
        </aside>

        {/* Preview stage */}
        <section className="flex-1 min-w-0 bg-[#090910] flex flex-col items-center justify-center p-5">
          <div className="relative w-full max-w-3xl aspect-video rounded-xl overflow-hidden border border-white/10 shadow-2xl" data-testid="preview-stage"
               style={{ background: "radial-gradient(circle at 60% 40%, rgba(124,112,255,0.55), transparent 55%), radial-gradient(circle at 35% 70%, rgba(236,72,153,0.35), transparent 50%), linear-gradient(160deg,#0a0620,#1a0b3a 60%,#050411)" }}>
            <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
            <div className="absolute left-4 bottom-3 font-mono text-xs text-violet-300">00:00:12:16 <span className="text-slate-500">/ 00:01:32:00</span></div>
          </div>
          {/* Transport */}
          <div className="mt-4 flex items-center gap-4 bg-[#12121E]/85 backdrop-blur-md px-6 py-2 rounded-full border border-white/10 shadow-2xl" data-testid="transport-bar">
            <button className="text-slate-400 hover:text-white" data-testid="transport-skip-back"><SkipBack className="w-4 h-4" /></button>
            <button className="text-slate-400 hover:text-white" data-testid="transport-prev"><StepBack className="w-4 h-4" /></button>
            <button onClick={() => setPlaying((p) => !p)} data-testid="transport-play"
                    className="w-9 h-9 rounded-full bg-[#5B4DFF] grid place-items-center text-white shadow-[0_0_16px_rgba(91,77,255,0.5)]">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
            </button>
            <button className="text-slate-400 hover:text-white" data-testid="transport-next"><StepForward className="w-4 h-4" /></button>
            <button className="text-slate-400 hover:text-white" data-testid="transport-skip-fwd"><SkipForward className="w-4 h-4" /></button>
            <div className="w-px h-5 bg-white/10" />
            <button className="flex items-center gap-1 text-xs text-slate-300" data-testid="transport-fit">Fit <ChevronDown className="w-3 h-3" /></button>
          </div>
        </section>

        {/* Inspector */}
        <aside className="w-80 shrink-0 bg-[#0F0F1A] border-l border-white/10 flex flex-col overflow-y-auto" data-testid="inspector-panel">
          <div className="flex border-b border-white/10">
            {["Video", "Audio", "Speed", "Animation"].map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`inspector-tab-${t.toLowerCase()}`}
                      className={cx("flex-1 py-3 text-xs font-medium border-b-2 transition", tab === t ? "text-white border-[#5B4DFF]" : "text-slate-500 border-transparent hover:text-slate-300")}>
                {t}
              </button>
            ))}
          </div>
          {/* Transform */}
          <div className="p-4 border-b border-white/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Transform</span>
              <ChevronDown className="w-4 h-4 text-slate-500" />
            </div>
            <TransformRow label="Scale">
              <input type="range" min="10" max="200" value={scale} onChange={(e) => setScale(+e.target.value)} data-testid="inspector-scale" className="w-32 accent-[#5B4DFF]" />
              <span className="text-xs font-mono text-slate-300 w-10 text-right">{scale}%</span>
            </TransformRow>
            <TransformRow label="Position">
              <input defaultValue="0" data-testid="inspector-pos-x" className="w-12 bg-[#1A1A2C] border border-white/10 rounded px-2 py-1 text-xs text-center" />
              <input defaultValue="0" data-testid="inspector-pos-y" className="w-12 bg-[#1A1A2C] border border-white/10 rounded px-2 py-1 text-xs text-center" />
            </TransformRow>
            <TransformRow label="Rotate">
              <input defaultValue="0" data-testid="inspector-rotate" className="w-16 bg-[#1A1A2C] border border-white/10 rounded px-2 py-1 text-xs text-center" />
              <span className="text-xs text-slate-500">°</span>
            </TransformRow>
          </div>
          {/* Compositing */}
          <div className="p-4 border-b border-white/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">Compositing</span>
              <ChevronDown className="w-4 h-4 text-slate-500" />
            </div>
            <div className="py-1.5">
              <div className="text-xs text-slate-400 mb-1">Blend Mode</div>
              <button className="w-full flex items-center justify-between bg-[#1A1A2C] border border-white/10 rounded-lg px-3 py-2 text-sm" data-testid="inspector-blend">
                Normal <ChevronDown className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <TransformRow label="Opacity">
              <input type="range" min="0" max="100" value={opacity} onChange={(e) => setOpacity(+e.target.value)} data-testid="inspector-opacity" className="w-32 accent-[#5B4DFF]" />
              <span className="text-xs font-mono text-slate-300 w-10 text-right">{opacity}%</span>
            </TransformRow>
          </div>
          {/* AI Enhance */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm"><Wand2 className="w-4 h-4 text-[#5B4DFF]" /> AI Enhance</div>
            <button onClick={() => setAiEnhance((v) => !v)} data-testid="inspector-ai-enhance"
                    className={cx("w-11 h-6 rounded-full p-0.5 transition", aiEnhance ? "bg-[#5B4DFF]" : "bg-white/10")}>
              <span className={cx("block w-5 h-5 rounded-full bg-white transition-transform", aiEnhance && "translate-x-5")} />
            </button>
          </div>
        </aside>
      </div>

      {/* ── Timeline dock ── */}
      <div className="h-64 shrink-0 bg-[#0A0A12] border-t border-white/10 flex flex-col select-none" data-testid="timeline-dock">
        {/* toolbar */}
        <div className="h-10 px-3 flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-1 text-slate-400">
            {[Undo2, Redo2, Scissors, Trash2, Copy, Split, Magnet].map((Ic, i) => (
              <button key={i} className="p-1.5 rounded-md hover:bg-white/10 hover:text-white" data-testid={`timeline-tool-${i}`}><Ic className="w-4 h-4" /></button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <ZoomOut className="w-4 h-4" />
            <input type="range" min="0" max="100" value={zoom} onChange={(e) => setZoom(+e.target.value)} data-testid="timeline-zoom" className="w-28 accent-[#5B4DFF]" />
            <ZoomIn className="w-4 h-4" />
          </div>
        </div>
        {/* ruler */}
        <div className="h-6 flex items-center px-24 border-b border-white/10 font-mono text-[10px] text-slate-500 justify-between relative">
          {RULER.map((r) => <span key={r}>{r}</span>)}
        </div>
        {/* tracks */}
        <div className="flex-1 overflow-y-auto relative">
          {/* playhead */}
          <div className="absolute top-0 bottom-0 left-[47%] w-[2px] bg-[#5B4DFF] shadow-[0_0_10px_#5B4DFF] z-30 pointer-events-none">
            <div className="w-3 h-3 -ml-1.5 rounded-sm bg-[#5B4DFF] rotate-45" />
          </div>

          {[
            { label: "Video 2", clips: V2_CLIPS, kind: "video" },
            { label: "Video 1", clips: V1_CLIPS, kind: "video" },
            { label: "Audio 1", kind: "audio" },
            { label: "Audio 2", kind: "audio" },
            { label: "Subtitles", kind: "subs" },
          ].map((row) => (
            <div key={row.label} className="h-11 border-b border-white/5 flex items-stretch">
              <div className="w-24 shrink-0 flex items-center px-3 text-xs text-slate-400 border-r border-white/5">{row.label}</div>
              <div className="relative flex-1">
                {row.kind === "video" && row.clips.map((c, i) => (
                  <div key={i} data-testid={`clip-${row.label}-${i}`} style={{ left: c.left, width: c.width, background: `linear-gradient(120deg, ${c.from}, ${c.to})` }}
                       className="absolute top-1.5 bottom-1.5 rounded-md border border-[#7C70FF]/70 shadow-[0_0_8px_rgba(124,112,255,0.4)] overflow-hidden" />
                ))}
                {row.kind === "audio" && (
                  <div className="absolute inset-x-1 top-2 bottom-2 rounded-md bg-emerald-500/10 border border-emerald-500/30 px-2 flex items-center">
                    <Waveform color="#10B981" />
                  </div>
                )}
                {row.kind === "subs" && SUBS.map((s, i) => (
                  <div key={i} data-testid={`subtitle-chip-${i}`} style={{ left: s.left, width: s.width }}
                       className="absolute top-1.5 bottom-1.5 rounded-md bg-[#FFB300]/15 border border-[#FFB300]/40 px-2 flex items-center gap-1.5 text-[11px] text-amber-100 truncate">
                    <Captions className="w-3 h-3 shrink-0 text-amber-300" /> <span className="truncate">{s.text}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
